<template>  
  <client-only>
    <div class="feedback-page" :style="{ height: pageHeight + 'px' }">
      <div
        class="dialog-container"
        :class="{ 'dialog-container-creator': businessId === 5 }"
      >
        <VirtualList
          ref="dialogWindow"
          class="dialog-window"
          :style="dialogWindowStyle"
          :data-key="'id'"
          :data-sources="messageList"
          :data-component="messageComponent"
          :extra-props="{ noHelpedList, post: axiosPost }"
          page-mode
          @totop="onScrollTotop"
        >
          <div v-show="overflow" slot="header" class="load-header">
            <VanLoading v-show="!finished" type="spinner" size="24" />
          </div>
        </VirtualList>
      </div>
      <Editor
        ref="editor"
        :hot-word-list="hotWordList"
        :post="axiosPost"
        :user-id="userId"
        :business-id="businessId"
        :query="query"
        :is-overseas="isOverseas"
        @input-focus="onFocusMsgInput"
        @send="onSendText"
        @send-hot-word="onSendHotWord"
        @send-file="onSendFile"
        @change-height="onChangeEditorHeight"
        @to-bottom="scrollToBottom"
      />
      <template v-if="businessId !== 5 && !isOverseas"
        ><van-popup
          v-model:show="showContactTooltip"
          :overlay="false"
          position="top"
          transition="van-fade"
        >
          <div class="contact-tooltip">
            {{ $t("inputContact") }}
          </div>
        </van-popup></template
      >
    </div>
  </client-only>
</template>
<script>
//import Vue from 'vue'
import { v4 as uuidv4 } from "uuid";
import unionBy from "lodash/unionBy";
import pick from "lodash/pick";
import intersectionBy from "lodash/intersectionBy";
import xorBy from "lodash/xorBy";
import VirtualList from "vue3-virtual-scroll-list";
import Message from "@/components/feedback/Message";
import Editor from "@/components/feedback/Editor";
import crypto from "@/utils/crypto";
import device from "@/utils/device";
import { rem2px } from "@/utils/formatter";
import EventBus from "@/utils/event-bus";
import isInKwaiying from "@ky/sdk/dist/isInKwaiying";
import { shallowRef } from "vue";
import { invoke } from "@yoda/bridge";
import kylogger from "@ky/sdk/dist/log/kylogger";
import callNative from "@ky/sdk/dist/callNative";
// 目前在 nuxt 中无法按需引入样式，因此采用手动引入的方式
import "vant/lib/index.css";
//
// js-bridge等ios全量之后才去掉
import {
  getNetworkState,
  getAppInfo,
  uploadUserMaterialZip,
} from "@/utils/ky-bridge";
// import { ImagePreview, Loading, Toast, Popup, Dialog } from 'vant'
import {
  showImagePreview,
  showToast,
  showConfirmDialog,
  Loading,
  Popup,
  Dialog,
} from "vant";
// 避免网络差的情况下，icon显示不出来
import "vant/lib/icon";
import { messageOriginMap, MessageType } from "@/utils/constant";
import canUseBridge from "@ky/sdk/dist/canUseBridge";
import getDeviceInfoSync from "@ky/sdk/dist/getDeviceInfoSync";

let pollInterval;
const PlatformMap = {
  android: 1,
  ios: 2,
  harmony: 3,
};

export default {
  components: {
    Editor,
    VirtualList,
  },
  setup() {
    if (process.server) {
      useHead({
      title: "",
      script: [{ src: "/js/flexible.js", type: "text/javascript" }],
      });
      return;
    }
    const { $t } = useNuxtApp();
    // console.info("[setup] ", process.server);
    // console.info("[setup] $t('feedBack'):", $t("all_feedback"));
    useHead({
      title: $t("all_feedback"),
      script: [{ src: "/js/flexible.js", type: "text/javascript" }],
    });
  },
  data() {
    return {
      orderMap: {},
      messageComponent: shallowRef(Message),
      pageHeight: 0,
      businessId: "",
      deviceId: "",
      userId: "",
      bussVersion: "",
      platVersion: "",
      messageList: [],
      hotWordList: [],
      appInfo: {
        networkType: "",
        platform: "",
        platVersion: "",
        bussVersion: "",
        deviceMode: "",
      },
      isPolling: false,
      isLoadingMore: false,
      shouldScrollBottom: false,
      editorHeight: 0,
      overflow: false,
      finished: false,
      showKeyboard: false,
      navBarStyle: {},
      noHelpedList: [],
      timer: null,
      showContactTooltip: false,
      host: "",
      isHarmony: /Harmony/i.test(window.navigator.userAgent), // 鸿蒙
      isOverseas: process.env.CODE_ENV === "spg",
    };
  },
  computed: {
    dialogWindowStyle() {
      return {
        paddingBottom: `${this.editorHeight + rem2px(0.5)}px`,
      };
    },
  },
  created() {
    const nuxtApp = useNuxtApp();
    if (process.client) {
      const vueApp = nuxtApp.vueApp;
      vueApp.use(Loading);
      vueApp.use(Popup);
      vueApp.use(Dialog);
    }
    const config = useRuntimeConfig();
    this.host = config.public.VITE_FEEDBACK_API_HOST;
    kylogger.sendImmediately("PV", "CUSTOMER_SERVICE");
  },
  async mounted() {
    // console.info("debug all_feedback", this.$t("all_feedback"));
    // let appInfo = await getAppInfo("all");
    // console.info("debug appInfo", typeof appInfo, appInfo);

    this.pageHeight = this.getWindowHeight();
    const { query } = this.$route;
    const businessOpts = { ky: 1, bigshot: 4, creator: 5, inch: 6 };
    this.businessId = businessOpts[query.appId] || businessOpts.ky;
    document.title =
      +this.businessId === 5
        ? this.$t("creatorServiceCenter")
        : this.$t("all_feedback");
    this.bussVersion = query.appVersion;
    this.platVersion = query.platVersion;

    window.addEventListener("resize", this.onResize);
    this.$nextTick(() => {
      this.$refs.dialogWindow.$el.addEventListener(
        "touchmove",
        this.resetPopup,
      );
      this.$refs.dialogWindow.$el.addEventListener("click", this.resetPopup);
      document.addEventListener("click", this.onClickBody);
    });
    EventBus.$on("send-question", this.onSendQuestion);
    EventBus.$on("view-image", this.onViewImage);
    EventBus.$on("no-help", this.onNoHelp);
    await this.getDeviceId();
    if (query?.vipOrder) {
      try {
        this.sendMessage({
          type: 9,
          content: query?.vipOrder,
        });
      } catch (e) {}
    }

    // 轮询消息列表
    this.$nextTick(() => {
      this.pollMessageList(true);
    });
    pollInterval = setInterval(this.pollMessageList, 1000);
    this.initAppInfo();
    if (this.isHarmony) {
      return;
    }
    this.getHotWordList();
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.onResize);

    if (this.$refs.dialogWindow) {
      this.$refs.dialogWindow.$el.removeEventListener(
        "touchmove",
        this.resetPopup,
      );
      this.$refs.dialogWindow.$el.removeEventListener("click", this.resetPopup);
    }

    EventBus.$off("send-question", this.onSendQuestion);
    EventBus.$off("view-image", this.onViewImage);
    EventBus.$off("no-help", this.onNoHelp);

    if (pollInterval) {
      clearInterval(pollInterval);
      pollInterval = null;
    }
  },
  methods: {
    onResize() {
      this.pageHeight = this.getWindowHeight();
    },
    axiosGet(url, params = {}) {
      const cryptoObj = crypto();
      console.log("===get", this.host, url, this.appInfo);
      return this.$request.get(`${this.host}${url}`, {
        params: {
          ...cryptoObj,
          bussId: this.businessId,
          deviceId: this.deviceId,
          did: this.deviceId,
          "ky.api_st": this.$cookie.get("ky.api_st") || "",
          userId: this.userId || "",
          platform: this.appInfo.platform,
          ...params,
        },
      });
    },
    axiosPost(url, data = {}, params = {}, options = {}) {
      const cryptoObj = crypto();
      return this.$request.post(`${this.host}${url}`, data, {
        params: {
          ...cryptoObj,
          bussId: this.businessId,
          deviceId: this.deviceId,
          did: this.deviceId,
          "ky.api_st": this.$cookie.get("ky.api_st") || "",
          userId: this.userId || "",
          ...params,
        },
        withCredentials: true,
        ...options,
      });
    },
    async pollMessageList(first) {
      if (this.isPolling) {
        return;
      }
      this.isPolling = true;
      const isInitial = !this.messageList.length;

      // 第一次进页面，获取最新消息
      if (isInitial) {
        this.shouldScrollBottom = true;
      }

      let messageList = await this.getMessageList(
        isInitial ? { up: true } : {},
      );
      console.log("=pollMessageList", messageList);
      if (first && messageList && messageList.length < 10) {
        this.finished = true;
      }

      if (this.shouldScrollBottom) {
        this.$nextTick(this.scrollToBottom);
      }
      this.isPolling = false;
    },
    async getMessageList(info = {}) {
      const params = {
        up: false,
        limit: 10,
        ...info,
      };
      try {
        if (this.messageList.length && !params.id) {
          params.id = params.up
            ? this.messageList[0].id
            : this.messageList[this.messageList.length - 1].id;
        }
        const res = await this.axiosGet(
          "/rest/n/fb/app/dialog/getMessage",
          params,
        );
        if (res.result !== 1 || !res.list.length) {
          this.shouldScrollBottom = false;
        }
        if (res.result === 1) {
          if (!params.up && res.list.some((item) => item.commonReply)) {
            this.handleContactTooltip();
          }
          return this.mergeMessageList(res.list, !params.up);
        }
      } catch (e) {
        console.error(e);
        this.shouldScrollBottom = false;
      }
    },
    async getHotWordList() {
      try {
        const res = await this.axiosGet("/rest/n/fb/app/dialog/getHotWord");
        if (res.result === 1) {
          this.hotWordList = res.listV2;
        }
      } catch (e) {}
    },
    async sendMessage(info) {
      info.messageOrigin = info.messageOrigin || messageOriginMap.Auto;
      try {
        const res = await this.axiosPost(
          "/rest/n/fb/app/dialog/sendMessage",
          { extra: JSON.stringify({}), ...info },
          {
            contact: localStorage.getItem("feedback_user_contact") || "",
            bussVersion: this.bussVersion || this.appInfo.bussVersion,
            appver: this.bussVersion || this.appInfo.bussVersion,
            platVersion: this.platVersion || this.appInfo.platVersion,
            ...pick(this.appInfo, ["networkType", "platform", "deviceMode"]),
          },
          { timeout: 10000 },
        );
        if (res.result === 1) {
          if (res.list.some((item) => item.commonReply)) {
            this.handleContactTooltip();
          }
          this.mergeMessageList(res.list, true);
          setTimeout(() => {
            this.scrollToBottom();
          }, 500);
        }
        return res.result;
      } catch (e) {
        if (e?.message === "Network Error") {
          showFailToast(this.$t("netError"));
        } else if (e?.message?.includes?.("timeout")) {
          showFailToast(this.$t("netTimeout"));
        }
      }
    },
    getDeviceInfoFromYoda(...args) {
      return new Promise((resolve, reject) => {
        invoke("system.getDeviceInfo").then((payload) => {
          if (payload && payload.sys) {
            resolve(payload);
          } else {
            reject(new Error("getDeviceInfoFromYoda failed"));
          }
        });
        setTimeout(reject, 500);
      });
    },
    getAppInfoFromYoda(...args) {
      return new Promise((resolve, reject) => {
        invoke("system.getAppInfo").then((payload) => {
          if (payload && payload.appver) {
            resolve(payload);
          } else {
            reject(new Error("getAppInfoFromYoda failed"));
          }
        });
        setTimeout(reject, 500);
      });
    },
    getNetworkTypeFromYoda(...args) {
      return new Promise((resolve, reject) => {
        invoke("system.getNetworkType").then((payload) => {
          if (payload && payload.net) {
            resolve(payload);
          } else {
            reject(new Error("getNetworkTypeFromYoda failed"));
          }
        });
        setTimeout(reject, 500);
      });
    },
    deviceInfoCompat() {
      if (device.isIOS) {
        Object.assign(this.appInfo, {
          platform: PlatformMap["ios"],
          platVersion: device.iosVersion,
        });
      } else if (device.isAndroid) {
        Object.assign(this.appInfo, {
          platform: PlatformMap["android"],
          platVersion: device.androidVersion,
        });
      } else if (this.isHarmony) {
        const info = window.navigator?.userAgent?.match?.(/Harmony (\d+\.\d+)/);
        Object.assign(this.appInfo, {
          platform: PlatformMap["harmony"],
          platVersion: info?.[1],
        });
      } else {
        this.appInfo.platform = 4;
      }
      this.appInfo.deviceMode = device.model;
    },
    appInfoCompat() {
      this.appInfo.bussVersion = device.appVersion;
    },
    async networkTypeCompat() {
      const net = await getAppInfo("net");
      this.appInfo.networkType = net;
      getNetworkState()
        .then((state) => {
          if (state === 1 || state === 2) {
            this.appInfo.networkType = state;
          }
        })
        .catch((e) => {});
    },
    yodaCompat() {
      this.networkTypeCompat();
      this.deviceInfoCompat();
      this.appInfoCompat();
    },
    async initAppInfo() {
      if (isInKwaiying() && !this.isHarmony) {
        // 通知客户端进入客服中心，触发自动上报日志
        if (canUseBridge("enterIMChat")) {
          callNative("enterIMChat");

          console.info("==isInYoda==");
        }
        try {
          this.getDeviceInfoFromYoda({})
            .then(({ sys, mod }) => {
              const [platform, platVersion] = sys.split("_");
              Object.assign(this.appInfo, {
                platform: platform.toLowerCase() === "android" ? 1 : 2,
                platVersion,
                deviceMode: mod,
              });
            })
            .catch(this.deviceInfoCompat);
          this.getAppInfoFromYoda({})
            .then(({ appver }) => {
              this.appInfo.bussVersion = appver;
            })
            .catch(this.appInfoCompat);
          this.getNetworkTypeFromYoda({})
            .then(({ net }) => {
              this.appInfo.networkType =
                { WIFI: 1, MOBILE_4G: 2, MOBILE_3G: 3 }[net] || "";
            })
            .catch(this.networkTypeCompat);
        } catch (e) {
          console.info("==initAppInfo error==");
          this.yodaCompat();
        }
      } else {
        this.yodaCompat();
      }
    },
    async getDeviceId() {
      const { query } = this.$route;
      let deviceId = query.did;
      const uid = query.userId || query.user_id || "";
      this.userId = uid;
      if (!deviceId && isInKwaiying()) {
        try {
          const { did, userId } = await this.getAppInfoFromYoda();
          console.info("app info: ", did, userId);
          deviceId = did;
          !uid && (this.userId = userId);
        } catch (e) {
          console.error("yoda get app info failed ");
        }
      }
      if (!deviceId) {
        try {
          deviceId = await getAppInfo("did");
        } catch (e) {}
      }
      if (!deviceId) {
        deviceId = localStorage.getItem("feedback_deviceId");
      }
      if (!deviceId) {
        deviceId = uuidv4();
      }
      localStorage.setItem("feedback_deviceId", deviceId);
      this.deviceId = deviceId;
    },
    async onSendText({ word: text, messageOrigin }) {
      console.log("=onSendText", text);
      const msg = text.trim();
      if (!msg) {
        return showToast(this.$t("inputEmpty"));
      }
      if (msg.length > 500) {
        return showToast(this.$t("maxLength"));
      }
      const res = await this.sendMessage({
        type: 1,
        content: msg,
        messageOrigin,
      });
      if (res === 1) {
        this.clearEditor();
      }
    },
    handleContactTooltip() {
      let time = localStorage.getItem("feedback_cotact_tooltip_time");
      if (time) {
        time = Number(time);
      }
      // 不展示填写联系方式的businessId列表
      const noContactId = [5];
      const now = Date.now();
      const isOverWeek = !time || now - time > 7 * 24 * 60 * 60 * 1000;
      if (
        !localStorage.getItem("feedback_user_contact") &&
        isOverWeek &&
        !noContactId.includes(this.businessId)
      ) {
        localStorage.setItem("feedback_cotact_tooltip_time", now);
        this.showContactTooltip = true;
      }
    },
    async onSendHotWord(w) {
      const info = { type: 1, content: w.word };
      if (device.isKy && w.showHint) {
        try {
          // 草稿授权弹窗，严格限制要在快影内
          await showConfirmDialog({
            title: this.$t("draftDialogTitle"),
            message: this.$t("draftDialogDesc"),
            confirmButtonText: this.$t("accept"),
            cancelButtonText: this.$t("deny"),
            confirmButtonColor: "rgba(255,255,255,0.9)",
            cancelButtonColor: "rgba(255,255,255,0.4)",
            getContainer: ".dialog-container",
          });
          // 调用jsbridge通知客户端上传草稿，回调草稿id
          const res = await uploadUserMaterialZip();
          console.log("上传草稿回调：", JSON.stringify(res));
          info.extra = JSON.stringify({
            projectId: (res && res.projectId) || "",
          });
          this.sendMessage(info);
        } catch (e) {
          console.error("e: ", e);
        }
      } else {
        this.sendMessage(info);
      }
    },
    onSendQuestion(question) {
      this.sendMessage({ type: 1, content: question });
    },
    onSendFile(info) {
      this.sendMessage(info);
    },
    clearEditor() {
      this.$refs.editor.clearInput();
    },
    getScrollTop() {
      let scrollTop = 0;
      let bodyScrollTop = 0;
      let documentScrollTop = 0;
      if (document.body) {
        bodyScrollTop = document.body.scrollTop;
      }
      if (document.documentElement) {
        documentScrollTop = document.documentElement.scrollTop;
      }
      scrollTop =
        bodyScrollTop - documentScrollTop > 0
          ? bodyScrollTop
          : documentScrollTop;
      return scrollTop;
    },
    getScrollHeight() {
      let scrollHeight = 0;
      let bodyScrollHeight = 0;
      let documentScrollHeight = 0;
      if (document.body) {
        bodyScrollHeight = document.body.scrollHeight;
      }
      if (document.documentElement) {
        documentScrollHeight = document.documentElement.scrollHeight;
      }
      scrollHeight =
        bodyScrollHeight - documentScrollHeight > 0
          ? bodyScrollHeight
          : documentScrollHeight;
      return scrollHeight;
    },
    getWindowHeight() {
      let windowHeight = 0;
      if (document.compatMode === "CSS1Compat") {
        windowHeight = document.documentElement.clientHeight;
      } else {
        windowHeight = document.body.clientHeight;
      }
      return windowHeight;
    },
    scrollToBottom() {
      this.scrollToOffset(this.$refs?.dialogWindow?.getScrollSize?.());
      /**
       * vue-virtual-scroll-list的原因第一次滚动到底部不是很准确，需要再滚动一次
       * 但这好像又会导致ios端editor偶尔会消失，@FIXME
       * 注释下面这句话好像也不会出现滚动到底部不准确的问题了(2021-06-29，iphone8)。先这样处理，再观察
       */
      //   setTimeout(() => {
      //     this.scrollToOffset(this.$refs.dialogWindow.getScrollSize())
      //   }, 100)
      this.shouldScrollBottom = false;
    },
    onFocusMsgInput() {
      if (device.isIOS) {
        this.scrollToBottom();
      } else {
        setTimeout(this.scrollToBottom, 500);
      }
      this.showKeyboard = true;
    },
    resetPopup() {
      this.showContactTooltip = false;
    },
    async onScrollTotop() {
      if (this.isLoadingMore) {
        return;
      }
      this.isLoadingMore = true;
      const messageList = await this.getMessageList({ up: true });
      if (messageList && !messageList.length) {
        this.finished = true;
      }
      if (messageList && messageList.length) {
        const ids = messageList.map((item) => item.id);
        this.$nextTick(() => {
          const dialogWindow = this.$refs.dialogWindow;
          const offset = ids.reduce((prevValue, currentId) => {
            const prevSize =
              typeof prevValue === "string"
                ? dialogWindow.getSize(prevValue)
                : prevValue;
            return prevSize + dialogWindow.getSize(currentId);
          });
          this.scrollToOffset(offset);
          this.isLoadingMore = false;
        });
      } else {
        this.isLoadingMore = false;
      }
    },
    scrollToOffset(offset) {
      if (this.$refs.dialogWindow) {
        this.$refs.dialogWindow.scrollToOffset(offset);
      }
    },
    getOrderId(item) {
      if (item.type === MessageType.refundCard) {
        const info = JSON.parse(item?.content || "{}");
        return info?.orderNo;
      }
      return "";
    },
    mergeMessageList(list, isNew) {
      const intersectionList = intersectionBy(list, this.messageList, "id");
      const appendList = xorBy(list, intersectionList, "id");
      if (isNew) {
        list.forEach((item, index) => {
          if (item.type === MessageType.refundCard) {
            const orderId = this.getOrderId(item);
            if (this.orderMap[orderId]) {
              const index = this.messageList.findIndex((msg) => {
                return this.getOrderId(msg) === orderId;
              });
              if (index > -1) {
                this.messageList.splice(index, 1);
              }
            }
            this.orderMap[orderId] = true;
          }
          this.messageList.push({ ...item, msgIndex: index });
        });
        this.messageList = unionBy(this.messageList, "id");
      } else {
        list.reverse().forEach((item, index) => {
          if (item.type === MessageType.refundCard) {
            const orderId = this.getOrderId(item);
            if (this.orderMap[orderId]) {
              return;
            }
            this.orderMap[orderId] = true;
          }
          this.messageList.unshift({ ...item, msgIndex: index });
        });
        this.messageList = unionBy(this.messageList, "id");
      }
      this.$nextTick(this.checkOverflow);
      return appendList;
    },
    onChangeEditorHeight(h) {
      this.editorHeight = h;
      // 避免ios端输入框高度变化时坠落被键盘挡住
      this.$nextTick(() => {
        if (!this.timer) {
          this.timer = setTimeout(() => {
            this.scrollToOffset(this.$refs?.dialogWindow?.getScrollSize?.());
            this.timer = null;
          }, 500);
        }
      });
    },
    onBack() {
      invoke("webview.exitWebView");
    },
    onViewImage(msg, type = "image") {
      if (type === "image") {
        const imageMsgs = this.messageList.filter((item) => item.type === 2);
        const index = imageMsgs.findIndex((item) => item.id === msg.id);
        showImagePreview({
          images: imageMsgs.map((item) => item.content),
          startPosition: index,
          loop: false,
        });
      } else if (type === "urlType") {
        showImagePreview({
          images: [msg],
          loop: false,
        });
      }
    },
    checkOverflow() {
      if (this.$refs.dialogWindow) {
        this.overflow =
          this.$refs.dialogWindow?.getScrollSize?.() -
            this.$refs.dialogWindow?.getClientSize?.() >
          0;
      } else {
        this.overflow = false;
      }
    },
    async onNoHelp(msg) {
      const res = await this.axiosPost("/rest/n/fb/app/dialog/updateHelp", {
        id: msg.id,
        help: 2,
      });
      if (res.result === 1) {
        showToast(this.$t("thanksFeedback"));
        this.noHelpedList.push(msg.id);
      }
    },
    onClickBody() {
      this.showContactTooltip = false;
    },
  },
  head: {
    title: "",
    meta: [
      {
        name: "viewport",
        content:
          "width=device-width,initial-scale=1.0,maximum-scale=1.0,user-scalable=0",
      },
    ],
  },
};
</script>
<style lang="less">
@font-family-default: PingFangSC-Regular;
@contact-bar-height: 1.19rem;
/*! purgecss start ignore */
:root {
  --van-white: #222;
  --van-text-color: rgba(255, 255, 255, 0.9);
  --van-border-color: #2f2f2f;
  --van-toast-text-color: rgba(255, 255, 255, 0.9);
}
@keyframes fade-enter {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

@keyframes fade-leave {
  from {
    opacity: 1;
  }

  to {
    opacity: 0;
  }
}

html,
body {
  width: 100%;
  height: 100%;
  -webkit-font-smoothing: antialiased;
  // background-color: #dcdcdc;
}

.feedback-page {
  display: flex;
  flex-direction: column;
  font-family: @font-family-default;
  font-size: 0.28rem;
  color: rgba(25, 25, 25, 1);
  // background-color: rgba(255, 255, 255, 1);
  background-color: #141414;
  .nav-bar {
    position: fixed;
    top: 0;
    left: 0;
    display: flex;
    align-items: center;
    width: 100%;
    height: 0.74rem;
    background-color: #fff;
    z-index: 999;
    img {
      margin-left: 0.15rem;
      width: 0.6rem;
      height: 0.6rem;
    }
    span {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      font-family: @font-family-default;
      font-weight: 700;
      font-size: 0.32rem;
      color: rgba(25, 25, 25, 0.9);
    }
  }
  #anchor {
    position: fixed;
    top: 0;
    left: 0;
    width: 4px;
    height: 4px;
    background-color: red;
    z-index: 1000;
  }
  .dialog-container {
    flex: 1;
    width: 100%;
    height: 100%;
    margin-top: @contact-bar-height + 0.2rem;
    &-creator {
      margin-top: 0.34rem;
    }
  }
  .dialog-window {
    flex: 1;
    padding: 0 0.34rem;
    width: 100%;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    // background-color: rgba(255, 255, 255, 1);
    background-color: #141414;
  }

  .load-header {
    display: flex;
    justify-content: center;
    padding: 0.1rem 0;
    .no-more {
      // color: rgba(25, 25, 25, 0.3);
      color: #b9b9b9;
      font-size: 0.24rem;
    }
  }
}
/*! purgecss start ignore */
.van-toast {
  color: "#fff";
  &.toast__long-text {
    width: 50%;
  }
}
/*! purgecss start ignore */
.van-popup.van-popup--top {
  padding-top: @contact-bar-height + 0.1rem;
  background-color: transparent;
  pointer-events: none;
  .contact-tooltip {
    margin-left: 2.56rem;
    padding: 0.37rem 0.3rem 0;
    width: 4.12rem;
    height: 1.42rem;
    font-size: 0.26rem;
    line-height: 0.36rem;
    color: rgba(0, 0, 0, 0.9);
    background-image: url("../../assets/img/feedback/popover-bg.png");
    background-size: cover;
  }
}
</style>
